/** @jsx jsx */
import { jsx } from "@emotion/react";
import { useRef } from "react";
import {
  useSpring,
  useSpringRef,
  useChain,
  animated,
  config,
} from "@react-spring/web";
import { useIdentityContext } from "react-netlify-identity-gotrue";

import appConfig from "../../modules/appConfig";
import VisuallyHidden from "../VisuallyHidden";
import OtherIssues from "../OtherIssues";

export const Cover = (): React.ReactElement => {
  const springRef1 = useSpringRef();
  const springRef2 = useSpringRef();
  const springRef3 = useSpringRef();
  const springRef4 = useSpringRef();

  const fadeOut = useSpring({
    from: { opacity: 1 },
    to: { opacity: 0 },
    delay: 500,
    config: config.slow,
    ref: springRef1,
  });

  const draw = useRef(true);

  const { x } = useSpring({
    from: { x: 0 },
    to: { x: 1 },
    config: {
      mass: 1,
      tension: 4,
      friction: 6,
      precision: 0.025,
    },
    loop: () => {
      draw.current = !draw.current;
      return true;
    },
    ref: springRef2,
  });

  const fadeIn1 = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
    config: config.molasses,
    ref: springRef3,
  });

  const fadeIn2 = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
    config: config.molasses,
    ref: springRef4,
  });

  useChain([springRef1, springRef2, springRef3, springRef4]);

  const identity = useIdentityContext();
  const { user } = identity;

  return (
    <div
      css={{
        minHeight: "100vh",
        paddingTop: "5vmax",
        paddingBottom: "5vh",
        paddingRight: "10vw",
        paddingLeft: "10vw",
        zIndex: 200,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        background: "black",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <animated.div
        style={fadeOut}
        css={{
          position: "absolute",
          top: "0",
          right: "0",
          bottom: "0",
          left: "0",
          zIndex: 300,
          backgroundColor: "black",
        }}
      />
      <h1
        css={{
          fontSize: "8.5vmin",
          color: "white",
          marginTop: 0,
          marginBottom: 0,
        }}
      >
        <span
          css={{
            display: "inline-block",
            backgroundColor: "black",
            padding: "0.1em 0.4em",
            marginRight: "0.4em",
            borderColor: "white",
            borderWidth: "0.1em",
            borderStyle: "solid",
          }}
        >
          {appConfig.theme}
        </span>
        Issue {appConfig.issue}/{appConfig.year}
      </h1>
      <div css={{ width: "120vw" }}>
        <animated.svg
          viewBox="0 150 2000 700"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeMiterlimit="1.5"
          fill="none"
        >
          <animated.path
            d="M0 686h410.652l-.254-354.944h-42.607v-20.06l90.571.007v49.923s24.338-53.629 99.297-50.457c66.616 2.819 58.795 89.732 58.795 89.732V686h104.945s-61.388-27.045-58.241-138.39c4.1-145.075 42.357-259.472 177.683-258.13 135.326 1.342 161.219 62.985 147.245 206.097-64.262.203-178.787 0-178.787 0s-15.844 116.843.426 150.336c13.212 27.196 59.113 22.3 78.415-.066 20.341-23.571 25.591-98.671 25.591-98.671l72.554-.064S981.229 651.049 921.706 686c61.075.226 114.234 0 114.234 0l74.39-180.517-67.77-174.395h-31.29l-.01-20.085h116.95l.03 20.637h-32.01l48.68 137.204 57.13-137.902h-27.55v-19.939h79.53l-.02 21.53h-31.01l-66.05 156.535L1232.64 686h89.48s-50.24-22.215-54.76-107.573c-6.13-115.785 23.33-198.076 52.14-233.904 28.8-35.828 75.27-55.778 147.15-55.117 71.88.661 117.74 16.13 147.91 69.401s17.32 179.664-5.9 239.925c-26.25 68.143-47.96 76.459-65.22 87.268H2010v24l-863.33.33V686h35.89l-60.1-157.295L1057.74 686h38.43v24H529.213l-.732-24h40.984V395.853s2.257-66.305-47.453-58.746c-55.479 8.436-63.419 78.722-64.054 105.521-.844 35.658 0 243.372 0 243.372l38.883.076V710H0"
            stroke="white"
            strokeWidth={12}
            opacity={0.2}
          />
          <animated.path
            d="M0 686h410.652l-.254-354.944h-42.607v-20.06l90.571.007v49.923s24.338-53.629 99.297-50.457c66.616 2.819 58.795 89.732 58.795 89.732V686h104.945s-61.388-27.045-58.241-138.39c4.1-145.075 42.357-259.472 177.683-258.13 135.326 1.342 161.219 62.985 147.245 206.097-64.262.203-178.787 0-178.787 0s-15.844 116.843.426 150.336c13.212 27.196 59.113 22.3 78.415-.066 20.341-23.571 25.591-98.671 25.591-98.671l72.554-.064S981.229 651.049 921.706 686c61.075.226 114.234 0 114.234 0l74.39-180.517-67.77-174.395h-31.29l-.01-20.085h116.95l.03 20.637h-32.01l48.68 137.204 57.13-137.902h-27.55v-19.939h79.53l-.02 21.53h-31.01l-66.05 156.535L1232.64 686h89.48s-50.24-22.215-54.76-107.573c-6.13-115.785 23.33-198.076 52.14-233.904 28.8-35.828 75.27-55.778 147.15-55.117 71.88.661 117.74 16.13 147.91 69.401s17.32 179.664-5.9 239.925c-26.25 68.143-47.96 76.459-65.22 87.268H2010v24l-863.33.33V686h35.89l-60.1-157.295L1057.74 686h38.43v24H529.213l-.732-24h40.984V395.853s2.257-66.305-47.453-58.746c-55.479 8.436-63.419 78.722-64.054 105.521-.844 35.658 0 243.372 0 243.372l38.883.076V710H0"
            stroke="white"
            strokeWidth={12}
            strokeDasharray={x.to(
              (x) =>
                `${draw.current ? "" : "0"} ${x * 10000} ${(1 - x) * 10000} ${
                  draw.current ? "" : "0"
                }`
            )}
          />
        </animated.svg>
      </div>
      <VisuallyHidden>Nexo Creative Writing Group</VisuallyHidden>
      <div
        css={{
          display: appConfig.requireAuth && !user ? "block" : "none",
        }}
      >
        <animated.p
          style={fadeIn1}
          css={{
            color: "white",
            fontSize: "1.5em",
            marginTop: "2em",
            marginBottom: "2em",
          }}
        >
          ...coming soon!
        </animated.p>
        <animated.div
          style={fadeIn2}
          css={{
            color: "white",
          }}
        >
          <p css={{ marginBottom: "1em" }}>
            While you wait, we invite you to check out our previous issues:
          </p>
          <OtherIssues />
        </animated.div>
      </div>
    </div>
  );
};

export default Cover;
